
import { useState } from 'react';
import { useToast } from "@/hooks/use-toast";
import { useInvoiceGeneration } from "@/hooks/campaign/payment/useInvoiceGeneration";
import { getCompanyDetailsFromStorage, saveCompanyDetailsToStorage } from './utils/quotation/companyDetailsUtils';
import { getBudgetCalculationsFromStorage, calculateTotalAmount } from './utils/quotation/budgetUtils';
import { getLocationsWithDuration } from './utils/quotation/locationUtils';
import { CompanyDetails } from './types/quotation';

export const useQuotationGeneration = () => {
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const { toast } = useToast();
  const { 
    invoiceData, 
    showInvoice, 
    setShowInvoice, 
    generateInvoice,
    downloadInvoice,
    setInvoiceData
  } = useInvoiceGeneration();
  
  const handleViewQuotation = async () => {
    console.log("useQuotationGeneration: Function called");
    
    // Prevent multiple simultaneous calls
    if (isGeneratingInvoice) {
      console.log("useQuotationGeneration: Generation already in progress, skipping");
      return;
    }
    
    console.log("useQuotationGeneration: Starting quotation generation");
    try {
      setIsGeneratingInvoice(true);
      
      // Get company details from storage using our utility function
      let companyDetails: CompanyDetails = getCompanyDetailsFromStorage();
      
      // Get dates from local storage
      const startDate = localStorage.getItem('startDate') || new Date().toISOString();
      const endDate = localStorage.getItem('endDate') || new Date().toISOString();
      
      // Get the selection mode and selected dates for specific dates mode
      const selectionMode = (localStorage.getItem('dateSelectionMode') || 'range') as 'range' | 'specific';
      const selectedDatesStr = localStorage.getItem('selectedDates');
      let selectedDatesArray: string[] = [];
      
      if (selectionMode === 'specific' && selectedDatesStr) {
        try {
          selectedDatesArray = JSON.parse(selectedDatesStr);
        } catch (e) {
          console.error('Failed to parse selectedDates:', e);
        }
      }
      
      // Add dates to company details
      companyDetails.startDate = startDate;
      companyDetails.endDate = endDate;
      companyDetails.dateSelectionMode = selectionMode;
      companyDetails.selectedDates = selectedDatesArray;
      
      console.log(`Retrieved dates: ${startDate} to ${endDate}, mode: ${selectionMode}, selected dates: ${selectedDatesArray.length}`);
      
      // Get budget calculations
      const budgetInfo = getBudgetCalculationsFromStorage();
      
      // Parse device count and duration
      const devices = Number(localStorage.getItem('totalDevices') || localStorage.getItem('devices')) || 0;
      const duration = Number(localStorage.getItem('duration')) || 3; // Default to 3 days if not set
      console.log(`Retrieved devices: ${devices}, duration: ${duration}`);
      
      // Get locations with duration info
      const locationsWithDuration = getLocationsWithDuration(duration, budgetInfo);
      
      // Calculate total amount
      const amount = calculateTotalAmount(devices, duration, budgetInfo);
      
      // Store the company details to localStorage to ensure persistence
      saveCompanyDetailsToStorage(companyDetails);
      
      // Generate invoice/quotation
      console.log("Calling generateInvoice with company details:", companyDetails);
      const invoice = await generateInvoice(
        "draft-campaign",  // No campaign ID yet as this is just a quotation
        companyDetails,
        amount,
        "quotation", // Mark as quotation
        devices,
        duration,
        locationsWithDuration,
        budgetInfo // Pass budget info directly
      );
      
      if (invoice && invoice.data) {
        console.log("Invoice data received:", invoice.data);
        setInvoiceData(invoice.data);
        setShowInvoice(true);
      } else {
        console.error("No data received from generateInvoice");
        toast({
          title: "Error",
          description: "Failed to generate quotation: No data received",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error generating quotation:", error);
      toast({
        title: "Error",
        description: "Failed to generate quotation. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGeneratingInvoice(false);
      console.log("Generation process completed");
    }
  };

  return {
    invoiceData,
    showInvoice,
    setShowInvoice,
    isGeneratingInvoice,
    handleViewQuotation,
    downloadInvoice
  };
};
